import { useMutation } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { ListItem } from '@material-ui/core'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { ImportUploadFileInfo, ParagraphRevision } from 'models/projectImport'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import { ErrorsType, WarningDialogType } from '..'
const cautionDescription =
    "These types of errors won't prevent an import into the project database, but are areas the author should be aware of. For example, if duplicate subparagraphs under the same parent paragraph are detected, then Specpoint will append them so that they are unique within the database."
const needsAttentionText =
    'These types of errors prevent an import into the project database. For example, your document may contain a paragraph which goes a level deeper than the MasterSpec template.'
const useStyle = makeStyles((theme: Theme) => ({
    titleContainer: {
        width: '98%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    dialogSubTtitle: {
        cursor: 'pointer',
        '& .MuiTypography-h6': {
            color: '#EB5757',
            fontSize: pxToRem(14),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    cardCarret: {
        color: '#304959',
        marginLeft: -theme.spacing(0.75),
        '&.MuiSvgIcon-root': {
            fontSize: pxToRem(20),
        },
        cursor: 'pointer',
    },
    chevronDownIcon: {
        transform: 'rotate(90deg)',
    },
    errorType: {
        display: 'flex',
        alignItems: 'center',
    },
    errorText: {
        display: 'flex',
        alignItems: 'baseline',
        marginLeft: theme.spacing(2),
        fontSize: pxToRem(14),
        paddingLeft: theme.spacing(2.5),
    },
    errorList: {
        margin: theme.spacing(1),
        fontSize: pxToRem(14),
        display: 'list-item',
    },
    message: {
        margin: theme.spacing(1),
        fontSize: pxToRem(14),
        marginTop: 0,
        paddingLeft: theme.spacing(2.5),
    },
    typeDescription: {
        fontSize: pxToRem(14),
        marginTop: theme.spacing(1),
        color: theme.palette.common.black,
    },
    zeroPaddingTop: {
        paddingTop: 0,
    },
    dialogTitle: {
        color: '#304959',
        fontSize: pxToRem(34),
    },
    iconContainer: {
        paddingRight: theme.spacing(1.25),
    },
    headingContainer: {
        width: 'auto',
    },
}))

export interface ImportIssuesDialogProps {
    warningDialogType: WarningDialogType
    selectedFile: Partial<ImportUploadFileInfo>
    isDisplayed: boolean
    dialogDisplayHandler: () => void
}
const paragraphNotPicketUp = 'Paragraph not picked up',
    unexpectedParagraphAdded = 'Unexpected paragraph added',
    unsupportedtype = 'Unsupported style',
    fountDuplicateInstance = 'Found duplicate instance',
    duplicateSustainability = 'Duplicate sustainability paragraph detected',
    containedTable = `The document(s) you uploaded contained a table`,
    itemToRemoveFromError = 'Duplicate Key Renamed',
    renamed = 'Renamed'

const filterByTypeAndPresentData = (
    paragraphRevisions: ParagraphRevision[],
    condition: Function
) => {
    return (
        paragraphRevisions
            ?.filter((item) =>
                item.children?.length === 0
                    ? condition(item)
                    : item.children.some((itemChild) => condition(itemChild))
            )
            .map((paragraph) => `${paragraph.numbering} - ${paragraph.text}`) ??
        []
    )
}
const ImportIssuesDialog: React.FC<ImportIssuesDialogProps> = ({
    warningDialogType,
    selectedFile,
    isDisplayed,
    dialogDisplayHandler,
}) => {
    const classes = useStyle()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [isDialogOpen, setIsDialogOpen] = React.useState(isDisplayed)
    const [issuesToShow, setIssuesToShow] = React.useState<ErrorsType[]>([])
    const [issueCount, setIssueCount] = React.useState<number>(0)

    const handleDialogDisplay = React.useCallback(() => {
        setIsDialogOpen((past) => !past)
        dialogDisplayHandler()
    }, [isDialogOpen, isDisplayed])

    const handleExpandError = React.useCallback(
        (index: number, state: boolean) => {
            setIssuesToShow((past) =>
                past.map((item, indexInner) => {
                    if (index === indexInner) {
                        return { ...item, expanded: state }
                    } else {
                        return item
                    }
                })
            )
        },
        [issuesToShow, setIssuesToShow]
    )
    const dialogContent = React.useMemo(() => {
        return (
            <GridContainer>
                <GridItem className={classes.titleContainer}>
                    <Typography component={'span'}>
                        Family - {`${issuesToShow[0]?.family}`}
                    </Typography>
                </GridItem>
                <GridItem>
                    {issuesToShow.map((item1, index1) => {
                        if (item1?.values?.length !== 0) {
                            return (
                                <>
                                    <span className={classes.errorType}>
                                        {
                                            <ChevronRightIcon
                                                className={clsx(
                                                    classes.cardCarret,
                                                    {
                                                        [classes.chevronDownIcon]:
                                                            item1?.expanded,
                                                    }
                                                )}
                                                data-testid={
                                                    item1?.expanded
                                                        ? 'ExpandMoreIcon'
                                                        : 'chevronRightIcon'
                                                }
                                                onClick={() =>
                                                    handleExpandError(
                                                        index1,
                                                        !item1?.expanded
                                                    )
                                                }
                                                onKeyDown={onEnterKeyDown(() =>
                                                    handleExpandError(
                                                        index1,
                                                        !item1?.expanded
                                                    )
                                                )}
                                                tabIndex={0}
                                            />
                                        }
                                        {item1.values.length !== 0 && (
                                            <DialogTitle
                                                className={
                                                    classes.dialogSubTtitle
                                                }
                                                onClick={() =>
                                                    handleExpandError(
                                                        index1,
                                                        !item1.expanded
                                                    )
                                                }
                                                onKeyDown={onEnterKeyDown(() =>
                                                    handleExpandError(
                                                        index1,
                                                        !item1.expanded
                                                    )
                                                )}
                                                tabIndex={0}
                                            >
                                                {item1.type} (
                                                {
                                                    (warningDialogType ===
                                                    'Needs Attention'
                                                        ? item1?.values?.filter(
                                                              (item) =>
                                                                  !item?.includes(
                                                                      'Duplicate Key Renamed'
                                                                  )
                                                          )
                                                        : item1.values
                                                    )?.length
                                                }
                                                )
                                            </DialogTitle>
                                        )}
                                    </span>
                                    {item1?.expanded &&
                                        item1?.values?.length !== 0 &&
                                        item1?.values &&
                                        item1?.message && (
                                            <Typography
                                                className={classes.message}
                                            >
                                                {item1?.message}
                                            </Typography>
                                        )}
                                    {item1?.expanded &&
                                        item1.values?.length !== 0 &&
                                        item1.values?.map(
                                            (itemValue, itemIndex) => {
                                                return (
                                                    <ListItem
                                                        key={`li-${itemValue}`}
                                                        className={clsx(
                                                            itemIndex === 0 &&
                                                                classes.zeroPaddingTop,
                                                            classes.errorList,
                                                            classes.errorText
                                                        )}
                                                    >
                                                        {itemValue?.replace(
                                                            /['"]+/g,
                                                            ''
                                                        )}
                                                    </ListItem>
                                                )
                                            }
                                        )}
                                </>
                            )
                        } else {
                            return null
                        }
                    })}
                </GridItem>
            </GridContainer>
        )
    }, [issuesToShow, handleExpandError])

    const onClickViewDifferences = React.useCallback(() => {
        if (selectedFile && selectedFile?.backConvertedDiffDocxUri) {
            if (!selectedFile?.diffFileExpired) {
                navigateToExternalLink(selectedFile?.backConvertedDiffDocxUri)
            } else {
                showGlobalSnackbar({
                    variables: {
                        message: `The differences file you're requesting has expired. These files expire after 30 days.`,
                        variant: 'error',
                        id: 'ProjectImportDiff_FileError',
                    },
                })
            }
        }
    }, [selectedFile, showGlobalSnackbar])

    const dialogHeading = React.useMemo(() => {
        return (
            <GridContainer direction="column">
                <GridItem>
                    <GridContainer justifyContent={'space-between'}>
                        <GridContainer className={classes.headingContainer}>
                            <GridItem className={classes.iconContainer}>
                                <ReportProblemOutlinedIcon htmlColor="#F0451F" />
                            </GridItem>
                            <GridItem>
                                <Typography className={classes.dialogTitle}>
                                    {`${warningDialogType} (${issueCount} ${
                                        issueCount > 1 ? 'Articles' : 'Article'
                                    })`}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                        {selectedFile &&
                            selectedFile?.backConvertedDiffDocxUri && (
                                <GridItem>
                                    <RoundButton
                                        size="small"
                                        hasShadow={false}
                                        color="#fff"
                                        borderColor="#304959"
                                        backgroundColor="#304959"
                                        data-testid="viewDifferencesButton"
                                        hoverColor="#091B32"
                                        onClick={onClickViewDifferences}
                                    >
                                        View Differences
                                    </RoundButton>
                                </GridItem>
                            )}
                    </GridContainer>
                </GridItem>
                <GridItem>
                    <Typography className={classes.typeDescription}>
                        {warningDialogType === 'Caution'
                            ? cautionDescription
                            : needsAttentionText}
                    </Typography>
                </GridItem>
            </GridContainer>
        )
    }, [warningDialogType, issueCount, onClickViewDifferences])

    React.useEffect(() => {
        const isForError = warningDialogType === 'Needs Attention'
        setIssueCount(
            (isForError
                ? selectedFile?.paragraphErrors?.length
                : selectedFile?.paragraphWarnings?.length) ?? 0
        )

        const paragraphRevisions =
            (isForError
                ? selectedFile?.paragraphErrors
                : selectedFile?.paragraphWarnings) ?? []

        const defaultDetails = {
            family: selectedFile?.title!,
            expanded: false,
            backConvertedDiffDocxUri: selectedFile?.backConvertedDiffDocxUri,
        }
        if (isForError) {
            const errorObject = {
                ...defaultDetails,
                type: !selectedFile?.isMasterSpecSection
                    ? 'Styling'
                    : 'Duplicate Properties',
                message: selectedFile?.isMasterSpecSection
                    ? `A "Property Key" is the value that comes before a colon. Duplicate property keys cannot appear at the same outline level under an Article. Please review the comparison document and re-upload a corrected document.`
                    : '',
                values: filterByTypeAndPresentData(
                    paragraphRevisions,
                    (x: ParagraphRevision) =>
                        (x.message !== itemToRemoveFromError &&
                            x.message.includes(paragraphNotPicketUp)) ||
                        (selectedFile?.isMasterSpecSection &&
                            x.message !== itemToRemoveFromError &&
                            x.message.includes(paragraphNotPicketUp) &&
                            x.message.includes(fountDuplicateInstance))
                ),
            }

            const errorsLocal = [
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) =>
                            x.message !== itemToRemoveFromError &&
                            x.message.includes(unexpectedParagraphAdded)
                    ),
                    type: unexpectedParagraphAdded,
                },
                errorObject,
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) =>
                            !x.message.includes(unexpectedParagraphAdded) &&
                            !x.message.includes(paragraphNotPicketUp) &&
                            !x.message.includes(fountDuplicateInstance) &&
                            !x.message.includes(itemToRemoveFromError)
                    ),
                    type: 'Others',
                },
            ]
            setIssuesToShow(errorsLocal ?? [])
        } else {
            const warningsLocal = [
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) =>
                            x.message.includes(unsupportedtype)
                    ),
                    type: unsupportedtype,
                },
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) => x.message.includes(renamed)
                    ),
                    type: 'Renamed Properties',
                    message: `A "Property Key" is the value that comes before a colon. Duplicate property keys cannot appear at the same outline level under an Article. Specpoint has automatically added a postfix to create unique keys.`,
                },
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) =>
                            x.message.includes(duplicateSustainability) ||
                            x.message.includes(fountDuplicateInstance)
                    ),
                    type: 'Others',
                },
                {
                    ...defaultDetails,
                    values: filterByTypeAndPresentData(
                        paragraphRevisions,
                        (x: ParagraphRevision) =>
                            x.message.includes(containedTable)
                    ),
                    type: containedTable,
                },
            ]
            setIssuesToShow(warningsLocal ?? [])
        }
    }, [warningDialogType, selectedFile])

    return (
        <ConfirmationDialog
            dialogHeading={dialogHeading}
            openDialog={isDialogOpen}
            handleDialogClose={handleDialogDisplay}
            dialogActionCallback={handleDialogDisplay}
            contentNode={dialogContent}
            contentOverflow
            size="large"
            cancelText=""
            confirmText="Close"
            confirmMessage=" "
            variant="controlledWidth"
        />
    )
}

export default ImportIssuesDialog
